import { cloneElement, isValidElement, useContext } from "react";
import {
  type FontSizeTokens,
  Button as TamaguiButton,
  XStack,
  createStyledContext,
  isWeb,
  styled,
  useTheme,
  withStaticProperties,
} from "tamagui";

import { size } from "@/theme/fonts";
import { isMobileWeb } from "@/utils/web";
import LoadingSpinner from "./animated/LoadingSpinner";
import withTracking from "./withTracking";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "tertiaryWithIcon"
  | "landingSuggestion"
  | "subMenu"
  | "surveyOption";

const ButtonContext = createStyledContext<{
  mode: "primary";
  fontSize: number;
  size?: FontSizeTokens;
}>({
  mode: "primary",
  fontSize: 13,
  size: undefined,
});

type VariantProps = {
  mode: ButtonVariant;
  loading: boolean;
  type?: "button" | "submit" | "reset";
};

// The frame's hoverStyle and disabledStyle colors don't reach the text or icon,
// so pass them down directly. https://github.com/tamagui/tamagui/issues/3722
const getContentStyle = (
  mode: ButtonVariant,
  isTertiary: boolean,
  disabled?: boolean,
) => ({
  ...(isTertiary &&
    !isMobileWeb() && {
      "$group-tertiaryButton-hover": { color: "$textLinkHover" },
    }),
  ...(disabled &&
    (mode === "primary" || mode === "secondary" || mode === "subMenu") && {
      color: "$textButtonDisabled",
    }),
});

const GradientButton = TamaguiButton.styleable<VariantProps>(
  (
    {
      children,
      icon,
      iconAfter,
      "aria-disabled": ariaDisabled,
      loading,
      type: buttonType = "button",
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const { mode } = useContext(ButtonContext) as { mode: ButtonVariant };
    const isTertiary = mode === "tertiary" || mode === "tertiaryWithIcon";
    const contentStyle = getContentStyle(
      mode,
      isTertiary,
      Boolean(ariaDisabled || props.disabled),
    );

    let zIndexedAndSpacedIcon = icon;
    let zIndexedAndSpacedIconAfter = iconAfter;
    if (typeof icon === "function") {
      // Tamagui does something twisted here, it's not precisely a Component
      zIndexedAndSpacedIcon = (props: any) =>
        icon({
          height: "$250",
          width: "$250",
          ...props,
          zIndex: 2,
          ...contentStyle,
        });
    } else if (isValidElement(icon)) {
      zIndexedAndSpacedIcon = cloneElement(icon, {
        zIndex: 2,
        ...contentStyle,
      } as any);
    }
    if (typeof iconAfter === "function") {
      zIndexedAndSpacedIconAfter = (props: any) =>
        iconAfter({
          ...props,
          zIndex: 2,
          ...contentStyle,
        });
    } else if (isValidElement(iconAfter)) {
      zIndexedAndSpacedIconAfter = cloneElement(iconAfter, {
        zIndex: 2,
        ...contentStyle,
      } as any);
    }
    if (loading) {
      zIndexedAndSpacedIcon = null;
      zIndexedAndSpacedIconAfter = null;
      let gradientStartOverride = null;
      let gradientEndOverride = null;
      if (mode === "primary") {
        gradientStartOverride = theme.fillButtonPrimary?.get();
        gradientEndOverride = theme.iconCta?.get();
      }
      children = (
        <LoadingSpinner
          zIndex={2}
          gradientStartOverride={gradientStartOverride}
          gradientEndOverride={gradientEndOverride}
          size={24}
        />
      );
    }
    return (
      <TamaguiButton
        ref={ref}
        {...props}
        {...(isTertiary && {
          group: "tertiaryButton",
          // group defaults to an inline-size container, which collapses content-sized buttons
          containerType: "normal",
        })}
        textProps={{ ...props.textProps, ...contentStyle }}
        {...(isWeb ? ({ type: buttonType } as any) : undefined)}
        aria-disabled={ariaDisabled || loading || undefined}
        icon={zIndexedAndSpacedIcon}
        iconAfter={zIndexedAndSpacedIconAfter}
        zIndex={0}
        position="relative"
      >
        {children}
      </TamaguiButton>
    );
  },
);

const StyledButton = styled(GradientButton, {
  context: ButtonContext,
  cursor: "pointer",
  borderRadius: "$150",
  gap: "$150",
  scaleIcon: 2, // size of icon viewbox relative to text size in px
  borderColor: "$borderColor",
  outlineWidth: 2,
  outlineOffset: 2,
  outlineColor: "$transparent",
  backgroundColor: "transparent",
  fontFamily: "$sans-serif",
  height: "$450",
  textProps: {
    fontSize: "$button",
    zIndex: 2,
    cursor: "inherit",
  },

  hoverStyle: {
    shadowColor: "$shadowColor",
    shadowOffset: { width: 0, height: 10 },
    shadowRadius: "10px -5px",
    transform: "scale(1.03)",
  },
  pressStyle: {
    backgroundColor: "$fillInput",
    transform: "unset",
    shadowColor: "$transparent",
  },
  focusStyle: {},
  focusVisibleStyle: {
    outlineColor: "$a11yBlue",
    outlineStyle: "solid",
  },
  disabledStyle: {
    cursor: "not-allowed",
  },
  variants: {
    mode: {
      primary: {
        scaleIcon: 1.5,
        color: "$textButtonPrimary",
        backgroundColor: "$fillButtonPrimary",
        borderWidth: 0,
        hoverStyle: {
          backgroundColor: "$buttonHighlight",
        },
        disabledStyle: {
          backgroundColor: "$fillButtonDisabled",
          color: "$textButtonDisabled",
          cursor: "not-allowed",
        },
        pressStyle: {
          // not in current mocks
          backgroundColor: "$buttonHighlight",
        },
      },
      secondary: {
        color: "$strokeButtonSecondary",
        backgroundColor: "transparent",
        borderColor: "$strokeButtonSecondary",
        borderWidth: "$strokeWidth",
        hoverStyle: {
          borderColor: "$fillPrimary",
          backgroundColor: "transparent",
        },
        pressStyle: {
          backgroundColor: "$navSelected",
          borderColor: "transparent",
        },
        disabledStyle: {
          borderColor: "$strokeButtonDisabled",
          color: "$textButtonDisabled",
        },
      },
      tertiary: {
        color: "$strokeButtonSecondary",
        backgroundColor: "transparent",
        borderWidth: 0,
        borderColor: "transparent",
        disabledStyle: {
          opacity: 0.4,
        },
        textProps: {
          textDecorationLine: "underline",
          textDecorationThickness: "2px",
          textUnderlineOffset: "2px",
          whiteSpace: "normal",
          flexWrap: "wrap",
        },
        hoverStyle: {
          shadowColor: "transparent",
          shadowOffset: { width: 0, height: 0 },
          shadowRadius: 0,
          transform: "none",
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: "$textLinkHover",
        },
      },
      tertiaryWithIcon: {
        color: "$strokeButtonSecondary",
        backgroundColor: "transparent",
        borderWidth: 0,
        borderColor: "transparent",
        disabledStyle: {
          opacity: 0.4,
        },
        hoverStyle: {
          shadowColor: "transparent",
          shadowOffset: { width: 0, height: 0 },
          shadowRadius: 0,
          transform: "none",
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: "$textLinkHover",
        },
      },
      landingSuggestion: {
        scaleIcon: 1,
        color: "$textPrompt",
        backgroundColor: "$fillPrompt",
        borderWidth: 0,
        borderBottomRightRadius: 0,
        fontWeight: 700,
        textProps: {
          fontSize: size.xs, // Regular font token $xs maps to Button context's size mapping, not our design system
        },
        hoverStyle: {
          transform: "scale(1.03)",
          backgroundColor: "$fillPrompt",
        },
        focusStyle: {
          backgroundColor: "$fillPrompt",
        },
      },
      // TODO: temp styles as variant for subMenu button component
      subMenu: {
        backgroundColor: "$transparent",
        borderRadius: "$150",
        borderWidth: 0,
        fontSize: "$sm",
        justifyContent: "flex-start",
        padding: "$100",
        height: "auto",
        hoverStyle: {
          backgroundColor: "$navHover",
          // override base hover shadow/lift
          transform: "none",
          shadowColor: "$transparent",
          shadowRadius: 0,
          shadowOffset: { width: 0, height: 0 },
        },
        disabledStyle: {
          backgroundColor: "$fillButtonDisabled",
          color: "$textButtonDisabled",
        },
        pressStyle: {
          backgroundColor: "$fillNeutral",
        },
      },
      surveyOption: {
        color: "$textPrimary",
        backgroundColor: "$fillCallout",
        borderWidth: "$strokeWidth",
        borderRadius: "$100",
        borderColor: "$strokeSubdued",
        height: "auto",
        minHeight: "$600",
        paddingVertical: "$300",
        paddingHorizontal: "$200",
        justifyContent: "flex-start",
        textProps: {
          whiteSpace: "normal",
        },
        hoverStyle: {
          // No hover effects
          shadowColor: "transparent",
          shadowOffset: { width: 0, height: 0 },
          shadowRadius: 0,
          transform: "none",
          backgroundColor: "$fillCallout",
          borderColor: "$strokeSubdued",
        },
        pressStyle: {
          backgroundColor: "$fillInput",
          borderColor: "$borderColor",
        },
      },
    },
    constrained: {
      true: {
        $lg: {
          maxWidth: "$3000",
          width: "100%",
        },
      },
    },
  } as const,
  defaultVariants: {
    mode: "primary",
  },
});

/** ButtonWrapper is a utility wrapper that provides button functionality to its children */
const ButtonWrapperFrame = styled(XStack, {
  role: "button",
  ...(isWeb ? { type: "button" } : { accessible: true }),
  context: ButtonContext,
  tabIndex: 0,
  cursor: "pointer",
  borderRadius: "$150",
  outlineOffset: "$25",
  focusVisibleStyle: {
    outlineColor: "$outlineColor",
    outlineStyle: "solid",
    outlineWidth: 2,
  },
  variants: {
    round: {
      true: {
        borderRadius: 100_000,
      },
    },
  },
});

// A `<div role="button">` doesn't get the native Enter/Space → click a real
// `<button>` has, and v2 can't use `render:"button"` (crashes on View-based
// components), so synthesize keyboard activation on web. Native is unaffected.
// This polyfill was put in place as of tamagui v2.1.0
const ButtonWrapper = ButtonWrapperFrame.styleable((props, ref) => (
  <ButtonWrapperFrame
    ref={ref}
    {...props}
    {...(isWeb && {
      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
        (
          props as { onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void }
        ).onKeyDown?.(e);
        if (!e.defaultPrevented && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.currentTarget.click();
        }
      },
    })}
  />
));

export const Button = withStaticProperties(
  withTracking(StyledButton, "button"),
  {
    Wrapper: withTracking(ButtonWrapper, "button"),
  },
);
